// src/pages/materials/components/CompositionPickerMobile.jsx
import React, { useMemo } from "react";
import {
  Button, InputNumber, Select, Space, Tag, Tooltip, Divider,
  Typography, Popconfirm, Segmented, Dropdown
} from "antd";
import {
  PlusOutlined, DeleteOutlined, ClearOutlined,
  CheckCircleTwoTone, ExclamationCircleTwoTone
} from "@ant-design/icons";
import { MATERIALS_UA } from "../catalog/materialsCatalog.ua";

const { Text } = Typography;
const DEFAULT_PRESETS = [100, 90, 80, 70, 60, 50, 40, 33, 30, 25, 20, 15, 10, 5];

// Прибираємо англійське в дужках
const rmParen = (s) => String(s || "").replace(/\s*\([^)]*\)\s*$/u, "").trim();

export default function CompositionPickerMobile({
  value,
  onChange,
  onCommit,                 // викликаємо ТІЛЬКИ нижньою кнопкою «Готово»
  optionsCatalog = MATERIALS_UA,
  presets = DEFAULT_PRESETS,
  maxParts = 6,
}) {
  const list = Array.isArray(value) ? value : [];
  const total = useMemo(
    () => round2(list.reduce((a, it) => a + (Number(it.percent) || 0), 0)),
    [list]
  );

  // live-оновлення (не видаляємо частково заповнені рядки)
  const setAt = (idx, patch) => {
    const next = list.map((it, i) =>
      i === idx
        ? { ...it, ...patch, name: patch.name != null ? rmParen(patch.name) : it.name }
        : it
    );
    onChange?.(sanitizeLive(next));
  };

  const addRow = () => {
    if (list.length >= maxParts) return;
    const next = [...list, { name: undefined, percent: undefined }];
    if (next.length === 1) next[0].percent = 100; // один — 100%
    onChange?.(next);
  };

  const removeAt = (idx) => onChange?.(sanitizeLive(list.filter((_, i) => i !== idx)));
  const clearAll = () => onChange?.([]);

  const handleDone = () => {
    const clean = sanitizeFinal(list);
    onCommit?.(clean.length ? clean : value);
  };

  const status = total === 100
    ? (<Space><CheckCircleTwoTone twoToneColor="#52c41a" /><Text type="success">Сума 100%</Text></Space>)
    : (<Space><ExclamationCircleTwoTone twoToneColor="#faad14" /><Text type="warning">Сума {total}% (залишок {round2(100 - total)}%)</Text></Space>);

  // Фільтр каталогу (відображаємо лише укр.)
  const [category, setCategory] = React.useState("all");
  const filteredOptions = useMemo(() => {
    const items = (optionsCatalog || [])
      .filter(Boolean)
      .map(m => ({ ...m, label: rmParen(m.label), value: m.value }));
    const groups = {
      premium: { label: "Преміум", options: [] },
      animal:  { label: "Тваринні", options: [] },
      plant:   { label: "Рослинні", options: [] },
      manmade: { label: "Штучні/Хімічні", options: [] },
    };
    items.forEach(m => { if (m.category && groups[m.category]) groups[m.category].options.push({ value: m.value, label: m.label }); });
    const all = [groups.premium, groups.animal, groups.plant, groups.manmade].filter(g => g.options.length);
    if (category === "all") return all;
    return all.filter(g => g.label === categoryMap[category]);
  }, [optionsCatalog, category]);

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={8}>
      {/* Статус + Очистити */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <Space>{status}</Space>
        <Popconfirm title="Очистити склад?" onConfirm={clearAll} okText="Очистити" cancelText="Скасувати">
          <Button size="small" danger icon={<ClearOutlined />}>Очистити</Button>
        </Popconfirm>
      </div>

      {/* Фільтр груп */}
      <Segmented
        block size="small" value={category} onChange={setCategory}
        options={[
          { label: "Усі", value: "all" },
          { label: "Преміум", value: "premium" },
          { label: "Тваринні", value: "animal" },
          { label: "Рослинні", value: "plant" },
          { label: "Штучні", value: "manmade" },
        ]}
      />

      {/* Додати рядок */}
      <Button type="dashed" block icon={<PlusOutlined />} onClick={addRow} disabled={list.length >= maxParts}>
        Додати компонент складу
      </Button>

      {/* Швидкі пропорції для 2-х компонентів */}
      {list.length === 2 && (
        <TwoTapRatiosToolbar
          onApply={(a, b) => onChange?.([{ ...list[0], percent: a }, { ...list[1], percent: b }])}
        />
      )}

      {/* Рядки складу */}
      {list.map((row, idx) => {
        const current = Number(row.percent) || 0;
        const maxAllowed = round2(current + Math.max(0, 100 - sum(list)));

        return (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: "8px 12px",
              flexWrap: "nowrap",
            }}
          >
            {/* Матеріал — портал у document.body */}
            <Select
              style={{ flex: 1, minWidth: 220 }}
              placeholder="Матеріал"
              value={toCatalogValue(row.name, optionsCatalog)}
              onChange={(v, opt) => setAt(idx, { name: fromCatalogValue(v, opt) })}
              options={filteredOptions}
              optionFilterProp="label"
              showSearch
              virtual
              listHeight={380}
              allowClear
              placement="bottomLeft"
              dropdownStyle={{ maxHeight: "60vh", overflow: "auto" }}
              getPopupContainer={() => document.body}                // ⬅️ портал
              popupClassName="composition-material-popup"
            />

            {/* Праворуч: % + видалити */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PercentPicker
                value={isFiniteNum(row.percent) ? row.percent : undefined}
                maxAllowed={maxAllowed}
                presets={presets}
                onPickQuick={(v) => setAt(idx, { percent: clamp0to100(v) })}
                onManualDone={(v) => setAt(idx, { percent: clamp0to100(v) })}
                onManualChange={(v) => setAt(idx, { percent: clamp0to100(v) })}
              />
              <Button danger onClick={() => removeAt(idx)} icon={<DeleteOutlined />} />
            </div>
          </div>
        );
      })}

      <Divider style={{ margin: "8px 0" }} />
      {/*<div style={{ display: "flex", justifyContent: "flex-end" }}>*/}
      {/*  <Button type="primary" onClick={handleDone}>Готово</Button>*/}
      {/*</div>*/}
    </Space>
  );
}

const categoryMap = {
  all: "Усі", premium: "Преміум", animal: "Тваринні", plant: "Рослинні", manmade: "Штучні/Хімічні",
};

function TwoTapRatiosToolbar({ onApply }) {
  return (
    <Space wrap>
      <Tag color="blue">Швидкі пропорції</Tag>
      <Button size="small" onClick={() => onApply(50, 50)}>50/50</Button>
      <Button size="small" onClick={() => onApply(60, 40)}>60/40</Button>
      <Button size="small" onClick={() => onApply(70, 30)}>70/30</Button>
      <Button size="small" onClick={() => onApply(80, 20)}>80/20</Button>
    </Space>
  );
}

/** Пікер відсотка на Dropdown: пресети закривають себе, ручний ввід — по «Готово» */
function PercentPicker({ value, presets, maxAllowed, onPickQuick, onManualDone, onManualChange }) {
  const [inner, setInner] = React.useState(value);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setInner(value); }, [value]);

  const quick = useMemo(() => {
    const within = (presets || []).map(Number).filter(p => p >= 0 && p <= 100).sort((a, b) => b - a);
    const allowed = within.filter(p => p <= maxAllowed);
    const remainder = Math.min(100, Math.max(0, round2(maxAllowed)));
    const hasRemainder = remainder > 0 && !allowed.includes(remainder);
    return { allowed, remainder: hasRemainder ? remainder : null };
  }, [presets, maxAllowed]);

  const handleQuick = (p) => {
    setInner(p);
    onPickQuick?.(p);
    setOpen(false); // закриваємо ТІЛЬКИ себе
  };

  const handleManualChange = (v) => {
    const clamped = safeClamp(v, 0, maxAllowed);
    setInner(clamped);
    onManualChange?.(clamped);
  };

  const handleManualDone = () => {
    const v = isFiniteNum(inner) ? inner : maxAllowed;
    onManualDone?.(v);
    setOpen(false); // закриваємо ТІЛЬКИ себе
  };

  const overlay = (
    <div
      className="composition-percent-popup"
      style={{
        padding: 8,
        width: 320,
        background: "#fff",                      // стабільний фон
        borderRadius: 8,
        boxShadow: "0 6px 16px rgba(0,0,0,0.15)",// тінь
        zIndex: 10000,
      }}
      onMouseDown={(e) => e.preventDefault()}    // не піднімаємо фокус нагору
    >
      <Space wrap style={{ display: "flex" }}>
        {quick.allowed.map(p => (
          <Button key={p} size="small" onClick={() => handleQuick(p)}>{p}%</Button>
        ))}
        {quick.remainder != null && (
          <Button size="small" onClick={() => handleQuick(quick.remainder)}>
            Решта {quick.remainder}%
          </Button>
        )}
      </Space>

      <Divider style={{ margin: "8px 8px" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <InputNumber
          value={isFiniteNum(inner) ? inner : undefined}
          onChange={handleManualChange}
          min={0}
          max={maxAllowed}
          addonAfter="%"
          style={{ width: 180 }}
          placeholder={`0..${maxAllowed}`}
        />
        <Tooltip title="Підтвердити значення">
          <Button onClick={handleManualDone}>Готово</Button>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => overlay}
      trigger={["click"]}
      getPopupContainer={() => document.body}   // портал
    >
      <Button onClick={() => setOpen(true)}>
        {isFiniteNum(inner) ? `${inner}%` : "Відсоток"}
      </Button>
    </Dropdown>
  );
}

/* utils */
function sum(arr) { return round2(arr.reduce((a, it) => a + (Number(it.percent) || 0), 0)); }
function round2(v) { return Math.round((Number(v) || 0) * 100) / 100; }
function isFiniteNum(v) { return typeof v === "number" && isFinite(v); }
function clamp0to100(v) { return safeClamp(v, 0, 100); }
function safeClamp(v, min, max) { const n = Number(v); if (!isFinite(n)) return undefined; return Math.min(max, Math.max(min, round2(n))); }

// live: прибираємо лише ПОВНІСТЮ порожні рядки (щоб новий не зникав)
function sanitizeLive(arr) {
  return (arr || []).filter(it => {
    const hasName = rmParen(it?.name);
    const hasPct  = isFiniteNum(it?.percent);
    return hasName || hasPct;
  });
}

// final: лишаємо тільки валідні пари (є і матеріал, і %)
function sanitizeFinal(arr) {
  return (arr || [])
    .filter(it => rmParen(it?.name) && isFiniteNum(it?.percent))
    .map(it => ({ name: rmParen(it.name), percent: Number(it.percent) }));
}

function toCatalogValue(name, catalog) {
  if (!name) return undefined;
  const clean = rmParen(name);
  const hit = (catalog || []).find(m => rmParen(m.label) === clean || m.value === clean);
  return hit ? hit.value : clean;
}
function fromCatalogValue(val, opt) {
  return opt && opt.label ? rmParen(opt.label) : rmParen(val);
}
